import React, { useState } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Text,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import { 
  AddCircle24Filled, 
  LayoutColumnTwoSplitRight24Regular, 
  Image20Regular,
  RadioButton24Filled,
  TextAlignLeft24Regular,
  ThumbLike24Regular,
  Calendar24Regular,
  ArrowSort24Regular,
  Table24Regular,
  ArrowUpload24Regular,
  TopSpeed24Regular,
  SlideMultiple24Regular,
  DismissCircle24Filled,
  MoreHorizontal20Regular,
} from '@fluentui/react-icons';
import { CopilotPane } from '../office-shared/copilot';
import CopilotFABSimple from './CopilotFABSimple';
import { 
  Question, 
  ChoiceQuestion as ChoiceQuestionType,
  TextQuestion as TextQuestionType,
  RatingQuestion as RatingQuestionType,
  DateQuestion as DateQuestionType,
  RankingQuestion as RankingQuestionType,
  LikertQuestion as LikertQuestionType,
  FileUploadQuestion as FileUploadQuestionType,
  NPSQuestion as NPSQuestionType,
  SectionQuestion as SectionQuestionType,
} from '../../../types/forms';
import { 
  ChoiceQuestion, 
  ChoiceQuestionView,
  TextQuestion,
  TextQuestionView,
  RatingQuestion,
  RatingQuestionView,
  DateQuestion,
  DateQuestionView,
  RankingQuestion,
  RankingQuestionView,
  LikertQuestion,
  LikertQuestionView,
  FileUploadQuestion,
  FileUploadQuestionView,
  NPSQuestion,
  NPSQuestionView,
  SectionQuestion,
  SectionQuestionView,
} from './questions';
import { ViewResponsesTest } from './ViewResponsesTest';
import { ViewResponses } from './ViewResponses';

const useStyles = makeStyles({
  sectionSeparator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: 'calc(100% + 160px)',
    backgroundColor: '#f5f5f5',
    padding: '12px 80px',
    marginTop: '32px',
    marginBottom: '32px',
    marginLeft: '-80px',
    marginRight: '-80px',
  },
  sectionSeparatorLabel: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#242424',
    whiteSpace: 'nowrap',
  },
  sectionMenuIcon: {
    color: '#242424',
    cursor: 'pointer',
    flexShrink: 0,
  },
  questionRestStateHover: {
    transition: 'background 0.2s',
    cursor: 'pointer',
    background: 'transparent',
    boxShadow: 'none',
    padding: '8px 0 8px 20px', // add left indent to align with form title/add button
    width: '100%',
    '&:hover': {
      background: tokens.colorNeutralBackground2Hover,
      boxShadow: 'none',
    },
  },
  container: {
    display: 'flex',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    background: 'linear-gradient(90deg, #b8d4ed 0%, #a8d9e8 25%, #9ddfe6 50%, #a0e4e6 75%, #a8e9e8 100%)',
  },
  backgroundImage: {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundImage: 'url("/assets/9ee42242f4c0a13cdfa3a68a7ca873799719de38.png")',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    opacity: 0.4,
    zIndex: 0,
  },
  gradientOverlay: {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(180deg, #f5f5f5 0%, rgba(245, 245, 245, 0.8) 8.042%, rgba(245, 245, 245, 0) 19.29%)',
    zIndex: 1,
  },
  mainContent: {
    position: 'relative',
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    padding: '24px',
    paddingLeft: '80px',
    paddingRight: '80px',
    zIndex: 2,
    overflow: 'hidden',
    minHeight: 0,
  },
  mainContentInner: {
    display: 'flex',
    gap: '24px',
    width: '100%',
    maxWidth: '1600px',
    overflow: 'hidden',
  },
  card: {
    flex: 1,
    minWidth: '600px',
    maxWidth: '1000px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.28), 0px 0px 2px 0px rgba(0,0,0,0.24)',
    padding: '80px 80px 80px 80px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
  editingCard: {
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    padding: '20px',
    marginBottom: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  restStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 0 8px 20px',
    marginLeft: '-20px',
    marginRight: '-20px',
    marginTop: '-20px',
    marginBottom: '0',
    cursor: 'pointer',
    transition: 'background 0.2s',
    background: 'transparent',
    '&:hover': {
      background: tokens.colorNeutralBackground2Hover,
    },
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  titleText: {
    flex: 1,
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '28px',
    fontWeight: 600,
    lineHeight: '36px',
    color: '#616161',
    padding: '0',
    marginBottom: '7px',
    minHeight: '36px',
  },
  titleInputWrapper: {
    backgroundColor: 'white',
    borderRadius: '4px',
    position: 'relative',
    minHeight: '64px',
    display: 'flex',
    alignItems: 'center',
    padding: '10px',
    gap: '8px',
    marginLeft: '20px',
  },
  titleInput: {
    flex: '1 1 0%',
    maxWidth: 'calc(100% - 40px)', // Prevent overlap with icon and padding
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '32px',
    fontWeight: 600,
    lineHeight: '40px',
    color: '#616161',
    border: 'none',
    background: 'transparent',
    outline: 'none',
    padding: '0',
    boxSizing: 'border-box',
  },
  titleInputBorder: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    height: '2px',
    backgroundColor: '#03787c',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
  },
  imageIcon: {
    fontSize: '20px',
    color: '#616161',
    cursor: 'pointer',
    flexShrink: 0,
  },
  subtitleText: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#616161',
    padding: '0',
    marginBottom: '43px',
    minHeight: '20px',
  },
  subtitleWrapper: {
    cursor: 'pointer',
  },
  subtitleInputWrapper: {
    backgroundColor: 'white',
    borderRadius: '4px',
    padding: '6px 10px',
    minHeight: '32px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    position: 'relative',
    marginLeft: '20px',
  },
  subtitleInputBorder: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    height: '2px',
    backgroundColor: '#03787c',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
  },
  subtitleInput: {
    width: 'calc(100% - 20px)', // Prevent overlap with padding
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#616161',
    border: 'none',
    background: 'transparent',
    outline: 'none',
    padding: '0',
    boxSizing: 'border-box',
    '::placeholder': {
      color: '#A6A6A6',
      opacity: 1,
    },
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    height: '44px',
  },
  optionButton: {
    minWidth: 'auto',
    height: '44px',
    padding: '10px 12px',
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '22px',
    background: 'linear-gradient(90deg, #107c80 0%, #116e7c 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    '& .fui-Button__icon': {
      fontSize: '24px',
      color: '#03787C',
    },
    '&:hover': {
      backgroundColor: 'rgba(16, 124, 128, 0.1)',
    },
    '&:active': {
      backgroundColor: 'rgba(16, 124, 128, 0.2)',
    },
  },
  divider: {
    width: '2px',
    height: '24px',
    backgroundColor: '#e0e0e0',
    margin: '0 4px',
  },
  copilotIcon: {
    width: '24px',
    height: '24px',
  },
  addQuestionButton: {
    minWidth: 'auto',
    height: '44px',
    padding: '10px 12px 10px 0px',
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '22px',
    background: 'linear-gradient(90deg, #107c80 0%, #116e7c 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    '& .fui-Button__icon': {
      fontSize: '24px',
      color: '#03787C',
    },
    '&:hover': {
      backgroundColor: 'rgba(16, 124, 128, 0.1)',
    },
  },
  addQuestionRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    marginTop: '40px',
    paddingLeft: '0',
  },
  templatesSidebar: {
    position: 'absolute',
    left: '48px',
    top: '270px',
    width: '64px',
    height: '102px',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    borderRadius: '100px',
    boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.14), 0px 0px 2px 0px rgba(0,0,0,0.12)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '30px 0',
    gap: '4px',
    zIndex: 3,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
  },
  templatesIcon: {
    width: '24px',
    height: '24px',
    color: '#321e10',
  },
  templatesText: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '10px',
    fontWeight: 400,
    lineHeight: '14px',
    color: '#321e10',
    textAlign: 'center',
  },
  questionTypesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '20px',
    width: '100%',
    marginBottom: '40px',
  },
  questionTypeButton: {
    height: '56px',
    padding: '16px 20px',
    backgroundColor: '#f5f5f5',
    border: '1px solid #03787c',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '12px',
    cursor: 'pointer',
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '14px',
    color: '#333333',
    transition: 'all 0.2s',
    minWidth: 0,
    '&:hover': {
      backgroundColor: '#fafafa',
      color: '#03787C',
      fontWeight: 600,
    },
    '&:active': {
      transform: 'translateY(0)',
    },
    '& .fui-Button__icon': {
      fontSize: '24px',
      color: '#03787C',
      flexShrink: 0,
    },
  },
  copilotPane: {
    width: '380px',
    minWidth: '380px',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0px 1px 2px 0px rgba(0,0,0,0.28), 0px 0px 2px 0px rgba(0,0,0,0.24)',
  },
});

// Copilot icon component matching Figma design
const CopilotIcon: React.FC<{ className?: string }> = ({ className }) => (
  <img 
    src="/src/assets/office/ribbon/Copilot.svg" 
    alt="Copilot" 
    className={className}
    style={{ width: '24px', height: '24px' }}
  />
);

interface FormsBodyProps {
  formTitle: string;
  onTitleChange: (title: string) => void;
  showViewResponses?: boolean;
  onBackFromResponses?: () => void;
}

const FormsBody: React.FC<FormsBodyProps> = ({ 
  formTitle, 
  onTitleChange, 
  showViewResponses: externalShowViewResponses,
  onBackFromResponses 
}) => {
  const styles = useStyles();
  const [isCopilotOpen, setIsCopilotOpen] = useState(false);
  const [showQuestionTypes, setShowQuestionTypes] = useState(false);
  const [showDataVizTest] = useState(false);
  const [showViewResponses, setShowViewResponses] = useState(false);
  const [formSubtitle, setFormSubtitle] = useState("");
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [isSubtitleEditing, setIsSubtitleEditing] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [openSectionMenuId, setOpenSectionMenuId] = useState<string | null>(null);
  const titleInputRef = React.useRef<HTMLInputElement>(null);
  const subtitleInputRef = React.useRef<HTMLInputElement>(null);
  const editingCardRef = React.useRef<HTMLDivElement>(null);
  const questionTypesRef = React.useRef<HTMLDivElement>(null);
  const editingQuestionRef = React.useRef<HTMLDivElement>(null);
  const addButtonsRef = React.useRef<HTMLDivElement>(null);

  const questionTypes = [
    { icon: <RadioButton24Filled />, label: 'Choice', type: 'choice' as const },
    { icon: <TextAlignLeft24Regular />, label: 'Text', type: 'text' as const },
    { icon: <ThumbLike24Regular />, label: 'Rating', type: 'rating' as const },
    { icon: <Calendar24Regular />, label: 'Date', type: 'date' as const },
    { icon: <ArrowSort24Regular />, label: 'Ranking', type: 'ranking' as const },
    { icon: <Table24Regular />, label: 'Likert', type: 'likert' as const },
    { icon: <ArrowUpload24Regular />, label: 'Upload File', type: 'file-upload' as const },
    { icon: <TopSpeed24Regular />, label: 'Net Promoter Score', type: 'nps' as const },
    { icon: <SlideMultiple24Regular />, label: 'Section', type: 'section' as const },
  ];

  // Helper function to render add question buttons with dynamic label
  const renderAddQuestionButtons = (buttonLabel: string, isEditingContext: boolean = false) => (
    <div ref={addButtonsRef} data-add-question-buttons style={{ marginTop: isEditingContext ? '8px' : '32px', marginBottom: '24px' }}>
      {!showQuestionTypes && (
        <div className={styles.addQuestionRow}>
          <Button
            appearance="transparent"
            icon={<AddCircle24Filled />}
            className={styles.addQuestionButton}
            onClick={() => setShowQuestionTypes(true)}
          >
            {buttonLabel}
          </Button>
          <span style={{ color: '#e1e1e1', fontSize: '14px' }}>|</span>
          <Button
            appearance="transparent"
            icon={<CopilotIcon className={styles.copilotIcon} />}
            className={styles.addQuestionButton}
            onClick={() => setIsCopilotOpen(true)}
          >
            {buttonLabel.startsWith('Insert') ? 'Insert with Copilot' : 'Add with Copilot'}
          </Button>
        </div>
      )}
      {showQuestionTypes && (
        <div ref={questionTypesRef} style={{ marginTop: '20px', paddingLeft: '0px' }}>
          <Button
            appearance="transparent"
            icon={<DismissCircle24Filled />}
            className={styles.optionButton}
            style={{ marginBottom: '20px' }}
            onClick={() => setShowQuestionTypes(false)}
          >
            {buttonLabel}
          </Button>
          <div className={styles.questionTypesGrid}>
            {questionTypes.map((type, index) => (
              <Button
                key={index}
                appearance="transparent"
                icon={type.icon}
                className={styles.questionTypeButton}
                onClick={() => handleAddQuestion(type.type)}
              >
                {type.label}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const handleAddQuestion = (type: string) => {
    // Determine insertion position
    let insertIndex = questions.length;
    if (editingQuestionId) {
      // Find the index of the currently editing question
      const currentIndex = questions.findIndex(q => q.id === editingQuestionId);
      if (currentIndex !== -1) {
        insertIndex = currentIndex + 1; // Insert right after the current question
      }
    }
    
    let newQuestion: Question | null = null;
    if (type === 'choice') {
      newQuestion = {
        id: `question-${Date.now()}`,
        type: 'choice',
        title: 'Question',
        required: false,
        order: insertIndex,
        options: [
          { id: `option-${Date.now()}-1`, text: 'Option 1' },
          { id: `option-${Date.now()}-2`, text: 'Option 2' },
        ],
        allowMultiple: false,
      } as ChoiceQuestionType;
    } else if (type === 'text') {
      newQuestion = {
        id: `question-${Date.now()}`,
        type: 'text',
        title: 'Question',
        required: false,
        order: insertIndex,
        isLongAnswer: false,
      } as TextQuestionType;
    } else if (type === 'rating') {
      newQuestion = {
        id: `question-${Date.now()}`,
        type: 'rating',
        title: 'Question',
        required: false,
        order: insertIndex,
        maxRating: 5,
        icon: 'star',
      } as RatingQuestionType;
    } else if (type === 'date') {
      newQuestion = {
        id: `question-${Date.now()}`,
        type: 'date',
        title: 'Question',
        required: false,
        order: insertIndex,
      } as DateQuestionType;
    } else if (type === 'ranking') {
      newQuestion = {
        id: `question-${Date.now()}`,
        type: 'ranking',
        title: 'Question',
        required: false,
        order: insertIndex,
        options: [
          { id: `option-${Date.now()}-1`, text: 'Option 1' },
          { id: `option-${Date.now()}-2`, text: 'Option 2' },
          { id: `option-${Date.now()}-3`, text: 'Option 3' },
        ],
      } as RankingQuestionType;
    } else if (type === 'likert') {
      newQuestion = {
        id: `question-${Date.now()}`,
        type: 'likert',
        title: 'Question',
        required: false,
        order: insertIndex,
        statements: ['Statement 1', 'Statement 2'],
        scale: { min: 1, max: 5 },
      } as LikertQuestionType;
    } else if (type === 'fileUpload') {
      newQuestion = {
        id: `question-${Date.now()}`,
        type: 'fileUpload',
        title: 'Question',
        required: false,
        order: insertIndex,
        allowedTypes: ['word', 'excel', 'powerpoint', 'pdf', 'image'],
        maxFileSize: 10,
        maxFiles: 1,
        maxSize: 10,
      } as FileUploadQuestionType;
    } else if (type === 'nps') {
      newQuestion = {
        id: `question-${Date.now()}`,
        type: 'nps',
        title: 'Question',
        required: false,
        order: insertIndex,
        lowLabel: 'Not at all likely',
        highLabel: 'Extremely likely',
      } as NPSQuestionType;
    } else if (type === 'section') {
      newQuestion = {
        id: `question-${Date.now()}`,
        type: 'section',
        title: 'Section',
        required: false,
        order: insertIndex,
        description: '',
      } as SectionQuestionType;
    }
    if (newQuestion) {
      setQuestions(prev => {
        // Insert the new question at the appropriate position
        const updated = [...prev];
        updated.splice(insertIndex, 0, newQuestion!);
        
        // Update the order property for all questions after the inserted one
        for (let i = insertIndex + 1; i < updated.length; i++) {
          updated[i] = { ...updated[i], order: i };
        }
        
        setTimeout(() => {
          if (addButtonsRef.current) {
            addButtonsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        return updated;
      });
      setEditingQuestionId(newQuestion.id);
      setShowQuestionTypes(false);
    }
  };

  const handleUpdateQuestion = (questionId: string, updatedQuestion: Question) => {
    setQuestions(questions.map(q => q.id === questionId ? updatedQuestion : q));
  };

  const handleDeleteQuestion = (questionId: string) => {
    setQuestions(questions.filter(q => q.id !== questionId));
  };

  const handleDeleteSectionOnly = (sectionId: string) => {
    // Remove only the section, keep all other questions
    setQuestions(questions.filter(q => q.id !== sectionId));
  };

  const handleDeleteSectionAndQuestions = (sectionId: string) => {
    const sectionIndex = questions.findIndex(q => q.id === sectionId);
    if (sectionIndex === -1) return;
    
    // Find the next section or end of list
    let nextSectionIndex = questions.findIndex((q, idx) => idx > sectionIndex && q.type === 'section');
    if (nextSectionIndex === -1) {
      nextSectionIndex = questions.length; // No next section, delete till end
    }
    
    // Remove the section and all questions until the next section
    const updatedQuestions = [
      ...questions.slice(0, sectionIndex),
      ...questions.slice(nextSectionIndex)
    ];
    
    setQuestions(updatedQuestions);
  };

  const handleDuplicateQuestion = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      const duplicated = { ...question, id: `question-${Date.now()}`, order: questions.length };
      setQuestions([...questions, duplicated]);
    }
  };

  const handleMoveQuestion = (questionId: string, direction: 'up' | 'down') => {
    const index = questions.findIndex(q => q.id === questionId);
    if (index === -1) return;
    
    if (direction === 'up' && index > 0) {
      const newQuestions = [...questions];
      [newQuestions[index], newQuestions[index - 1]] = [newQuestions[index - 1], newQuestions[index]];
      setQuestions(newQuestions);
    } else if (direction === 'down' && index < questions.length - 1) {
      const newQuestions = [...questions];
      [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
      setQuestions(newQuestions);
    }
  };

  const handleTitleChange = (newTitle: string) => {
    // Only sync to header if there's actual text (not empty/placeholder)
    onTitleChange(newTitle || "Untitled form");
  };

  const handleTitleClick = () => {
    setIsTitleEditing(true);
    setIsSubtitleEditing(false);
    setTimeout(() => titleInputRef.current?.focus(), 0);
  };

  // Exit editing mode when clicking outside the editing card
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editingCardRef.current && !editingCardRef.current.contains(event.target as Node)) {
        setIsTitleEditing(false);
        setIsSubtitleEditing(false);
      }
    };

    if (isTitleEditing || isSubtitleEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTitleEditing, isSubtitleEditing]);

  // Exit question types view when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (questionTypesRef.current && !questionTypesRef.current.contains(event.target as Node)) {
        setShowQuestionTypes(false);
      }
    };

    if (showQuestionTypes) {
      document.addEventListener('mousedown', handleClickOutside);
      // Auto-scroll to question types grid when it appears
      setTimeout(() => {
        if (questionTypesRef.current) {
          questionTypesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showQuestionTypes]);

  // Exit question editing mode when clicking outside (but not on another question)
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is on another question view
      const target = event.target as HTMLElement;
      const isQuestionViewClick = target.closest('[data-question-view]');
      
      // Check if click is on a dropdown or its options (Fluent UI portals)
      const isDropdownClick = target.closest('[role="listbox"]') || 
                             target.closest('[role="option"]') ||
                             target.closest('.fui-Dropdown') ||
                             target.closest('[data-fui-portal]');
      
      // Check if click is on add question buttons
      const isAddQuestionButton = target.closest('[data-add-question-buttons]');
      
      if (editingQuestionRef.current && 
          !editingQuestionRef.current.contains(event.target as Node) &&
          !isQuestionViewClick &&
          !isDropdownClick &&
          !isAddQuestionButton) {
        setEditingQuestionId(null);
      }
    };

    if (editingQuestionId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingQuestionId]);

  // If showing data visualization test, render that instead
  if (showDataVizTest) {
    return <ViewResponsesTest />;
  }

  // If showing view responses page, render that instead
  if (showViewResponses || externalShowViewResponses) {
    return (
      <ViewResponses 
        onBack={() => {
          setShowViewResponses(false);
          onBackFromResponses?.();
        }}
        formTitle={formTitle || "Untitled form"}
      />
    );
  }

  return (
    <div className={styles.container}>
      {/* Background image */}
      <div className={styles.backgroundImage} />
      
      {/* Gradient overlay that blends from header */}
      <div className={styles.gradientOverlay} />

      {/* Templates Sidebar - hidden when copilot is open */}
      {!isCopilotOpen && (
        <div className={styles.templatesSidebar}>
          <LayoutColumnTwoSplitRight24Regular className={styles.templatesIcon} />
          <Text className={styles.templatesText}>Templates</Text>
        </div>
      )}

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.mainContentInner} style={{ justifyContent: isCopilotOpen ? 'flex-start' : 'center' }}>
          <div className={styles.card}>
          {/* Editing state shows grey background wrapper */}
          {(isTitleEditing || isSubtitleEditing) ? (
            <div className={styles.editingCard} ref={editingCardRef}>
              {/* Title - Editing state */}
              <div className={styles.titleContainer}>
                <div className={styles.titleInputWrapper}>
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={formTitle}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="Let's get started! What's your form about?"
                    className={styles.titleInput}
                  />
                  <Image20Regular className={styles.imageIcon} />
                  {isTitleEditing && <div className={styles.titleInputBorder} />}
                </div>
              </div>

              {/* Subtitle - Editing state */}
              <div 
                className={styles.subtitleInputWrapper}
                onClick={() => {
                  setIsSubtitleEditing(true);
                  setIsTitleEditing(false);
                  setTimeout(() => subtitleInputRef.current?.focus(), 0);
                }}
              >
                <input
                  ref={subtitleInputRef}
                  type="text"
                  value={formSubtitle}
                  onChange={(e) => setFormSubtitle(e.target.value)}
                  placeholder="A good subtitle is like a trailer - it's a great chance to let your audience know what the form is about and get better responses."
                  className={styles.subtitleInput}
                />
                {isSubtitleEditing && <div className={styles.subtitleInputBorder} />}
              </div>
            </div>
          ) : (
            <div className={styles.restStateContainer} onClick={handleTitleClick}>
              {/* Title - Rest state */}
              <div className={styles.titleText}>
                {formTitle || "Let's get started! What's your form about?"}
              </div>

              {/* Subtitle - Rest state */}
              {formSubtitle && (
                <div className={styles.subtitleText}>
                  {formSubtitle}
                </div>
              )}
            </div>
          )}
          
          {/* Render Questions */}
          {questions.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', marginTop: '20px' }}>
              {questions.map((question, index) => {
                const isEditing = editingQuestionId === question.id;
                const isLastQuestion = index === questions.length - 1;
                const shouldShowAddButtons = isEditing || (isLastQuestion && !editingQuestionId);
                const addButtonLabel = isEditing && !isLastQuestion ? 'Insert new question' : 'Add new question';
                const questionStyle = !isLastQuestion && !isEditing ? { marginBottom: '72px' } : {};
                
                if (question.type === 'choice') {
                  if (isEditing) {
                    return (
                      <React.Fragment key={question.id}>
                        <div ref={editingQuestionRef} style={questionStyle}>
                          <ChoiceQuestion
                            question={question as ChoiceQuestionType}
                            questionNumber={index + 1}
                            onChange={(updated) => handleUpdateQuestion(question.id, updated)}
                            onDelete={() => {
                              handleDeleteQuestion(question.id);
                              setEditingQuestionId(null);
                            }}
                            onDuplicate={() => handleDuplicateQuestion(question.id)}
                            onMoveUp={() => handleMoveQuestion(question.id, 'up')}
                            onMoveDown={() => handleMoveQuestion(question.id, 'down')}
                          />
                        </div>
                        {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, true)}
                      </React.Fragment>
                    );
                  }
                  
                  return (
                    <React.Fragment key={question.id}>
                      <div style={questionStyle} className={styles.questionRestStateHover}>
                        <ChoiceQuestionView
                          question={question as ChoiceQuestionType}
                          questionNumber={index + 1}
                          onClick={() => setEditingQuestionId(question.id)}
                        />
                      </div>
                      {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, false)}
                    </React.Fragment>
                  );
                } else if (question.type === 'text') {
                  if (isEditing) {
                    return (
                      <React.Fragment key={question.id}>
                        <div ref={editingQuestionRef} style={questionStyle}>
                          <TextQuestion
                            question={question as TextQuestionType}
                            questionNumber={index + 1}
                            onChange={(updated) => handleUpdateQuestion(question.id, updated)}
                            onDelete={() => {
                              handleDeleteQuestion(question.id);
                              setEditingQuestionId(null);
                            }}
                            onDuplicate={() => handleDuplicateQuestion(question.id)}
                            onMoveUp={() => handleMoveQuestion(question.id, 'up')}
                            onMoveDown={() => handleMoveQuestion(question.id, 'down')}
                          />
                        </div>
                        {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, true)}
                      </React.Fragment>
                    );
                  }
                  
                  return (
                    <React.Fragment key={question.id}>
                      <div style={questionStyle} className={styles.questionRestStateHover}>
                        <TextQuestionView
                          question={question as TextQuestionType}
                          questionNumber={index + 1}
                          onClick={() => setEditingQuestionId(question.id)}
                        />
                      </div>
                      {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, false)}
                    </React.Fragment>
                  );
                } else if (question.type === 'rating') {
                  if (isEditing) {
                    return (
                      <React.Fragment key={question.id}>
                        <div ref={editingQuestionRef} style={questionStyle}>
                          <RatingQuestion
                            question={question as RatingQuestionType}
                            questionNumber={index + 1}
                            onChange={(updated) => handleUpdateQuestion(question.id, updated)}
                            onDelete={() => {
                              handleDeleteQuestion(question.id);
                              setEditingQuestionId(null);
                            }}
                            onDuplicate={() => handleDuplicateQuestion(question.id)}
                            onMoveUp={() => handleMoveQuestion(question.id, 'up')}
                            onMoveDown={() => handleMoveQuestion(question.id, 'down')}
                          />
                        </div>
                        {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, true)}
                      </React.Fragment>
                    );
                  }
                  
                  return (
                    <React.Fragment key={question.id}>
                      <div style={questionStyle} className={styles.questionRestStateHover}>
                        <RatingQuestionView
                          question={question as RatingQuestionType}
                          questionNumber={index + 1}
                          onClick={() => setEditingQuestionId(question.id)}
                        />
                      </div>
                      {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, false)}
                    </React.Fragment>
                  );
                } else if (question.type === 'date') {
                  if (isEditing) {
                    return (
                      <React.Fragment key={question.id}>
                        <div ref={editingQuestionRef} style={questionStyle}>
                          <DateQuestion
                            question={question as DateQuestionType}
                            questionNumber={index + 1}
                            onChange={(updated) => handleUpdateQuestion(question.id, updated)}
                            onDelete={() => {
                              handleDeleteQuestion(question.id);
                              setEditingQuestionId(null);
                            }}
                            onDuplicate={() => handleDuplicateQuestion(question.id)}
                            onMoveUp={() => handleMoveQuestion(question.id, 'up')}
                            onMoveDown={() => handleMoveQuestion(question.id, 'down')}
                          />
                        </div>
                        {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, true)}
                      </React.Fragment>
                    );
                  }
                  
                  return (
                    <React.Fragment key={question.id}>
                      <div style={questionStyle} className={styles.questionRestStateHover}>
                        <DateQuestionView
                          question={question as DateQuestionType}
                          questionNumber={index + 1}
                          onClick={() => setEditingQuestionId(question.id)}
                        />
                      </div>
                      {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, false)}
                    </React.Fragment>
                  );
                } else if (question.type === 'ranking') {
                  if (isEditing) {
                    return (
                      <React.Fragment key={question.id}>
                        <div ref={editingQuestionRef} style={questionStyle}>
                          <RankingQuestion
                            question={question as RankingQuestionType}
                            questionNumber={index + 1}
                            onChange={(updated) => handleUpdateQuestion(question.id, updated)}
                            onDelete={() => {
                              handleDeleteQuestion(question.id);
                              setEditingQuestionId(null);
                            }}
                            onDuplicate={() => handleDuplicateQuestion(question.id)}
                            onMoveUp={() => handleMoveQuestion(question.id, 'up')}
                            onMoveDown={() => handleMoveQuestion(question.id, 'down')}
                          />
                        </div>
                        {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, true)}
                      </React.Fragment>
                    );
                  }
                  
                  return (
                    <React.Fragment key={question.id}>
                      <div style={questionStyle} className={styles.questionRestStateHover}>
                        <RankingQuestionView
                          question={question as RankingQuestionType}
                          questionNumber={index + 1}
                          onClick={() => setEditingQuestionId(question.id)}
                        />
                      </div>
                      {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, false)}
                    </React.Fragment>
                  );
                } else if (question.type === 'likert') {
                  if (isEditing) {
                    return (
                      <React.Fragment key={question.id}>
                        <div ref={editingQuestionRef} style={questionStyle}>
                          <LikertQuestion
                            question={question as LikertQuestionType}
                            questionNumber={index + 1}
                            onChange={(updated) => handleUpdateQuestion(question.id, updated)}
                            onDelete={() => {
                              handleDeleteQuestion(question.id);
                              setEditingQuestionId(null);
                            }}
                            onDuplicate={() => handleDuplicateQuestion(question.id)}
                            onMoveUp={() => handleMoveQuestion(question.id, 'up')}
                            onMoveDown={() => handleMoveQuestion(question.id, 'down')}
                          />
                        </div>
                        {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, true)}
                      </React.Fragment>
                    );
                  }
                  
                  return (
                    <React.Fragment key={question.id}>
                      <div style={questionStyle} className={styles.questionRestStateHover}>
                        <LikertQuestionView
                          question={question as LikertQuestionType}
                          questionNumber={index + 1}
                          onClick={() => setEditingQuestionId(question.id)}
                        />
                      </div>
                      {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, false)}
                    </React.Fragment>
                  );
                } else if (question.type === 'fileUpload') {
                  if (isEditing) {
                    return (
                      <React.Fragment key={question.id}>
                        <div ref={editingQuestionRef} style={questionStyle}>
                          <FileUploadQuestion
                            question={question as FileUploadQuestionType}
                            questionNumber={index + 1}
                            onChange={(updated) => handleUpdateQuestion(question.id, updated)}
                            onDelete={() => {
                              handleDeleteQuestion(question.id);
                              setEditingQuestionId(null);
                            }}
                            onDuplicate={() => handleDuplicateQuestion(question.id)}
                            onMoveUp={() => handleMoveQuestion(question.id, 'up')}
                            onMoveDown={() => handleMoveQuestion(question.id, 'down')}
                          />
                        </div>
                        {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, true)}
                      </React.Fragment>
                    );
                  }
                  
                  return (
                    <React.Fragment key={question.id}>
                      <div style={questionStyle} className={styles.questionRestStateHover}>
                        <FileUploadQuestionView
                          question={question as FileUploadQuestionType}
                          questionNumber={index + 1}
                          onClick={() => setEditingQuestionId(question.id)}
                        />
                      </div>
                      {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, false)}
                    </React.Fragment>
                  );
                } else if (question.type === 'nps') {
                  if (isEditing) {
                    return (
                      <React.Fragment key={question.id}>
                        <div ref={editingQuestionRef} style={questionStyle}>
                          <NPSQuestion
                            question={question as NPSQuestionType}
                            questionNumber={index + 1}
                            onChange={(updated) => handleUpdateQuestion(question.id, updated)}
                            onDelete={() => {
                              handleDeleteQuestion(question.id);
                              setEditingQuestionId(null);
                            }}
                            onDuplicate={() => handleDuplicateQuestion(question.id)}
                            onMoveUp={() => handleMoveQuestion(question.id, 'up')}
                            onMoveDown={() => handleMoveQuestion(question.id, 'down')}
                          />
                        </div>
                        {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, true)}
                      </React.Fragment>
                    );
                  }
                  
                  return (
                    <React.Fragment key={question.id}>
                      <div style={questionStyle} className={styles.questionRestStateHover}>
                        <NPSQuestionView
                          question={question as NPSQuestionType}
                          questionNumber={index + 1}
                          onClick={() => setEditingQuestionId(question.id)}
                        />
                      </div>
                      {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, false)}
                    </React.Fragment>
                  );
                } else if (question.type === 'section') {
                  const sectionNumber = questions.slice(0, index).filter(q => q.type === 'section').length + 1;
                  if (isEditing) {
                    return (
                      <React.Fragment key={question.id}>
                        <div className={styles.sectionSeparator}>
                          <div className={styles.sectionSeparatorLabel}>
                            Section {sectionNumber}
                          </div>
                          <Menu
                            positioning="below-end"
                            open={openSectionMenuId === question.id}
                            onOpenChange={(_e, data) => setOpenSectionMenuId(data.open ? question.id : null)}
                          >
                            <MenuTrigger disableButtonEnhancement>
                              <MoreHorizontal20Regular className={styles.sectionMenuIcon} style={{ cursor: 'pointer' }} />
                            </MenuTrigger>
                            <MenuPopover>
                              <MenuList>
                                <MenuItem
                                  onClick={() => {
                                    handleDuplicateQuestion(question.id);
                                    setOpenSectionMenuId(null);
                                  }}
                                >
                                  Duplicate section
                                </MenuItem>
                                <MenuItem
                                  onClick={() => {
                                    handleDeleteSectionOnly(question.id);
                                    setEditingQuestionId(null);
                                    setOpenSectionMenuId(null);
                                  }}
                                >
                                  Remove section only
                                </MenuItem>
                                <MenuItem
                                  onClick={() => {
                                    handleDeleteSectionAndQuestions(question.id);
                                    setEditingQuestionId(null);
                                    setOpenSectionMenuId(null);
                                  }}
                                >
                                  Remove section and questions
                                </MenuItem>
                              </MenuList>
                            </MenuPopover>
                          </Menu>
                        </div>
                        <div ref={editingQuestionRef} style={questionStyle}>
                          <SectionQuestion
                            question={question as SectionQuestionType}
                            questionNumber={index + 1}
                            onChange={(updated) => handleUpdateQuestion(question.id, updated)}
                            onDelete={() => {
                              handleDeleteSectionOnly(question.id);
                              setEditingQuestionId(null);
                            }}
                            onDuplicate={() => handleDuplicateQuestion(question.id)}
                            onMoveUp={() => handleMoveQuestion(question.id, 'up')}
                            onMoveDown={() => handleMoveQuestion(question.id, 'down')}
                            canMoveUp={index > 0}
                            canMoveDown={index < questions.length - 1}
                          />
                        </div>
                        {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, true)}
                      </React.Fragment>
                    );
                  }
                  
                  return (
                    <React.Fragment key={question.id}>
                      <div className={styles.sectionSeparator}>
                        <div className={styles.sectionSeparatorLabel}>
                          Section {sectionNumber}
                        </div>
                        <Menu
                          positioning="below-end"
                          onOpenChange={(_e, data) => setOpenSectionMenuId(data.open ? question.id : null)}
                        >
                          <MenuTrigger disableButtonEnhancement>
                            <MoreHorizontal20Regular className={styles.sectionMenuIcon} style={{ cursor: 'pointer' }} />
                          </MenuTrigger>
                          <MenuPopover>
                            <MenuList>
                              <MenuItem
                                onClick={() => {
                                  handleDuplicateQuestion(question.id);
                                  setOpenSectionMenuId(null);
                                }}
                              >
                                Duplicate section
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  handleDeleteSectionOnly(question.id);
                                  setOpenSectionMenuId(null);
                                }}
                              >
                                Remove section only
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  handleDeleteSectionAndQuestions(question.id);
                                  setOpenSectionMenuId(null);
                                }}
                              >
                                Remove section and questions
                              </MenuItem>
                            </MenuList>
                          </MenuPopover>
                        </Menu>
                      </div>
                      <div style={questionStyle} className={styles.questionRestStateHover}>
                        <SectionQuestionView
                          question={question as SectionQuestionType}
                          questionNumber={index + 1}
                          onClick={() => setEditingQuestionId(question.id)}
                        />
                      </div>
                      {shouldShowAddButtons && renderAddQuestionButtons(addButtonLabel, false)}
                    </React.Fragment>
                  );
                }
                return null;
              })}
            </div>
          )}
          
          {/* Remove the old add question buttons section */}
          
          {/* Initial state - no questions */}
          {questions.length === 0 && !showQuestionTypes ? (
            <div className={styles.optionsContainer}>
              <div className={styles.optionRow}>
                <Button
                  appearance="transparent"
                  icon={<AddCircle24Filled />}
                  className={styles.optionButton}
                  onClick={() => setShowQuestionTypes(true)}
                >
                  Quick start with
                </Button>
                
                <div className={styles.divider} />
                
                <Button
                  appearance="transparent"
                  icon={<CopilotIcon className={styles.copilotIcon} />}
                  className={styles.optionButton}
                  onClick={() => setIsCopilotOpen(true)}
                >
                  Draft with Copilot
                </Button>
              </div>
            </div>
          ) : null}
          
          {/* Initial question types grid - shown when no questions */}
          {questions.length === 0 && showQuestionTypes && (
            <div ref={questionTypesRef}>
              <Button
                appearance="transparent"
                icon={<DismissCircle24Filled />}
                className={styles.optionButton}
                style={{ marginBottom: '20px' }}
                onClick={() => setShowQuestionTypes(false)}
              >
                Add new question
              </Button>
              <div className={styles.questionTypesGrid}>
                {questionTypes.map((type, index) => (
                  <Button
                    key={index}
                    appearance="transparent"
                    icon={type.icon}
                    className={styles.questionTypeButton}
                    onClick={() => handleAddQuestion(type.type)}
                  >
                    {type.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CopilotPane with Form Generation */}
        {isCopilotOpen && (
          <div className={styles.copilotPane}>
            <CopilotPane
              isOpen={isCopilotOpen}
              onClose={() => setIsCopilotOpen(false)}
              appType="forms"
            />
          </div>
        )}
        </div>
      </div>

      {/* Copilot FAB - Only shown when Copilot pane is closed */}
      {!isCopilotOpen && (
        <CopilotFABSimple onClick={() => setIsCopilotOpen(true)} />
      )}
    </div>
  );
};

export default FormsBody;
